/**
 * Use Cache Management Hook
 *
 * Custom hooks for managing Redis cache with TanStack Query.
 * Handles caching, refetching, mutations, and error states automatically.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type UseQueryResult,
  type UseMutationResult,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import {
  fetchCacheOverview,
  fetchNamespaceStatus,
  fetchNamespaceKeys,
  invalidateNamespace,
  bulkInvalidateNamespaces,
  fetchCacheValue,
  deleteCacheKey,
  type CacheValueResponse,
} from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  CacheOverviewResponse,
  CacheNamespace,
  InvalidateResponse,
  BulkInvalidateResponse,
  NamespaceStatusResponse,
  NamespaceKeysResponse,
  DeleteCacheKeyResponse,
} from '@/types/cache';

/**
 * Use Cache Overview
 * Fetches overview of all caches with Redis stats
 *
 * @returns TanStack Query result with cache overview data
 */
export function useCacheOverview(): UseQueryResult<CacheOverviewResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['cache', 'overview'],
    queryFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchCacheOverview(token);
    },
    staleTime: 30000, // 30 seconds - cache stats change frequently
    gcTime: 300000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * Use Namespace Status
 * Fetches status for a specific cache namespace
 *
 * @param namespace - Cache namespace to get status for (null to disable)
 * @returns TanStack Query result with namespace status
 */
export function useNamespaceStatus(
  namespace: CacheNamespace | null
): UseQueryResult<NamespaceStatusResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['cache', 'namespace', namespace],
    queryFn: async () => {
      if (!namespace) {
        throw new Error('No namespace provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchNamespaceStatus(namespace, token);
    },
    enabled: namespace !== null,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    retry: 2,
  });
}

/**
 * Use Namespace Keys
 * Fetches paginated list of all keys in a namespace using infinite query
 *
 * @param namespace - Cache namespace to list keys from (null to disable)
 * @param limit - Number of keys per page (default 50)
 * @returns TanStack Infinite Query result with namespace keys
 */
export function useNamespaceKeys(
  namespace: CacheNamespace | null,
  limit: number = 50
): UseInfiniteQueryResult<{ pages: NamespaceKeysResponse[]; pageParams: (string | undefined)[] }> {
  const { getIdToken } = useAuth();

  return useInfiniteQuery({
    queryKey: ['cache', 'keys', namespace, limit],
    queryFn: async ({ pageParam }) => {
      if (!namespace) {
        throw new Error('No namespace provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchNamespaceKeys(namespace, token, pageParam, limit);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => {
      // Return the next cursor if there are more pages
      if (lastPage.hasMore && lastPage.nextCursor) {
        return lastPage.nextCursor;
      }
      return undefined;
    },
    enabled: namespace !== null,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false, // Don't auto-refetch as the list may be long
  });
}

/**
 * Invalidate Namespace Mutation Variables
 */
interface InvalidateNamespaceVariables {
  namespace: CacheNamespace;
  id?: string;
}

/**
 * Use Invalidate Namespace
 * Mutation hook for invalidating a cache namespace
 *
 * @returns TanStack Mutation result
 */
export function useInvalidateNamespace(): UseMutationResult<
  InvalidateResponse,
  Error,
  InvalidateNamespaceVariables
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ namespace, id }: InvalidateNamespaceVariables) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return invalidateNamespace(namespace, token, id);
    },
    onSuccess: (_data, variables) => {
      // Invalidate all cache-related queries to refresh counts
      queryClient.invalidateQueries({ queryKey: ['cache', 'overview'] });
      queryClient.invalidateQueries({ queryKey: ['cache', 'namespace', variables.namespace] });
      // If a specific ID was invalidated, also invalidate that lookup
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: ['cache', 'lookup', variables.namespace, variables.id],
        });
      }
    },
  });
}

/**
 * Use Bulk Invalidate
 * Mutation hook for invalidating multiple cache namespaces
 *
 * @returns TanStack Mutation result
 */
export function useBulkInvalidate(): UseMutationResult<
  BulkInvalidateResponse,
  Error,
  CacheNamespace[]
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (namespaces: CacheNamespace[]) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return bulkInvalidateNamespaces(namespaces, token);
    },
    onSuccess: (_data, namespaces) => {
      // Invalidate cache overview
      queryClient.invalidateQueries({ queryKey: ['cache', 'overview'] });
      // Invalidate each namespace's status
      namespaces.forEach(namespace => {
        queryClient.invalidateQueries({ queryKey: ['cache', 'namespace', namespace] });
      });
    },
  });
}

/**
 * Use Invalidate All Caches
 * Convenience hook for invalidating all cache namespaces at once
 *
 * @returns TanStack Mutation result
 */
export function useInvalidateAllCaches(): UseMutationResult<BulkInvalidateResponse, Error, void> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      const allNamespaces: CacheNamespace[] = [
        'menu',
        'modifier-display',
        'modifier-global',
        'vehicle-reference',
      ];

      return bulkInvalidateNamespaces(allNamespaces, token);
    },
    onSuccess: () => {
      // Invalidate all cache-related queries
      queryClient.invalidateQueries({ queryKey: ['cache'] });
    },
  });
}

/**
 * Use Cache Value
 * Fetches the actual cache value for a specific key
 *
 * @param key - Full cache key to fetch (null to disable)
 * @returns TanStack Query result with cache value data
 */
export function useCacheValue(key: string | null): UseQueryResult<CacheValueResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['cache', 'value', key],
    queryFn: async () => {
      if (!key) {
        throw new Error('Cache key is required');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchCacheValue(key, token);
    },
    enabled: key !== null && key.trim().length > 0,
    staleTime: 10000, // 10 seconds - cache values may change
    gcTime: 60000, // 1 minute - shorter cache for values
    retry: 1,
  });
}

/**
 * Delete Cache Key Mutation Variables
 */
interface DeleteCacheKeyVariables {
  key: string;
  namespace: CacheNamespace;
}

/**
 * Use Delete Cache Key
 * Mutation hook for deleting a specific cache key
 *
 * @returns TanStack Mutation result
 */
export function useDeleteCacheKey(): UseMutationResult<
  DeleteCacheKeyResponse,
  Error,
  DeleteCacheKeyVariables
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key }: DeleteCacheKeyVariables) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return deleteCacheKey(key, token);
    },
    onSuccess: (_data, variables) => {
      // Invalidate cache overview to refresh counts
      queryClient.invalidateQueries({ queryKey: ['cache', 'overview'] });
      // Invalidate namespace status
      queryClient.invalidateQueries({ queryKey: ['cache', 'namespace', variables.namespace] });
      // Invalidate the keys list for this namespace to refresh
      queryClient.invalidateQueries({ queryKey: ['cache', 'keys', variables.namespace] });
      // Invalidate the specific cache value query if it exists
      queryClient.invalidateQueries({ queryKey: ['cache', 'value', variables.key] });
    },
  });
}
